/**
 * NIGHTBOUND - in-game developer panel.
 *
 * Only rendered when the server reports `debugTools: true` in /api/health, and
 * the endpoints behind it 404 outside development. It drives the same public
 * actions the game does; nothing here is a special case in the simulation.
 */

import { useCallback, useEffect, useState } from 'react'
import { MOODS } from '../../shared/mood'
import type { MoodId } from '../../shared/species'
import { api } from '../api/client'
import { useGame } from '../state/GameProvider'
import { Button, Chip, cx } from './ui'
import { Modal } from './Modal'

const MOOD_IDS = Object.keys(MOODS) as MoodId[]

const NEED_FIELDS = [
  { key: 'hunger', label: 'Fed' },
  { key: 'energy', label: 'Energy' },
  { key: 'hygiene', label: 'Clean' },
  { key: 'bond', label: 'Bond' },
  { key: 'sanity', label: 'Sanity' },
  { key: 'fitness', label: 'Fitness' },
] as const

const QUICK_ADVANCE = [1, 6, 12, 24, 72]

export function DebugPanel() {
  const {
    debugEnabled,
    creature,
    pending,
    devAdvance,
    devSetNeeds,
    devSetMood,
    devSetSleep,
    devClearCooldowns,
    devResetTime,
  } = useGame()

  const [open, setOpen] = useState(false)
  const [offsetHours, setOffsetHours] = useState(0)
  const [customHours, setCustomHours] = useState(8)
  const [needs, setNeeds] = useState<Record<string, number>>({})

  const loadStatus = useCallback(async () => {
    try {
      const status = await api.devStatus()
      setOffsetHours(status.timeOffsetHours)
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (open) void loadStatus()
  }, [open, loadStatus])

  useEffect(() => {
    if (!creature) return
    setNeeds({
      hunger: Math.round(creature.needs.hunger),
      energy: Math.round(creature.needs.energy),
      hygiene: Math.round(creature.needs.hygiene),
      bond: Math.round(creature.needs.bond),
      sanity: Math.round(creature.needs.sanity),
      fitness: Math.round(creature.needs.fitness),
    })
  }, [creature])

  if (!debugEnabled) return null

  const runAdvance = async (hours: number) => {
    await devAdvance(hours)
    void loadStatus()
  }

  const busy = pending !== null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-3 z-40 rounded-[3px] border border-cold-500/60 bg-night-900/90 px-2.5 py-1.5 nb-label text-[9px] text-cold-400 shadow-lg backdrop-blur hover:bg-night-800 lg:bottom-5 lg:right-5"
        aria-label="Open developer tools"
      >
        DEV
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Developer tools"
        description="Development build only. Simulated time uses the ordinary offline-progression engine."
      >
        <div className="space-y-5">
          <section className="space-y-2">
            <h3 className="nb-label">Simulated time</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="neutral">offset {offsetHours >= 0 ? '+' : ''}{offsetHours}h</Chip>
              {QUICK_ADVANCE.map((hours) => (
                <Button
                  key={hours}
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => void runAdvance(hours)}
                >
                  +{hours}h
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                max={2880}
                value={customHours}
                onChange={(event) => setCustomHours(Number(event.target.value))}
                className="w-24 rounded-[3px] border border-ash-600/60 bg-night-950/70 px-2 py-1.5 nb-numeric text-xs text-bone-200"
                aria-label="Custom hours to advance"
              />
              <Button size="sm" disabled={busy} onClick={() => void runAdvance(customHours)}>
                Advance
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={async () => {
                  await devResetTime()
                  void loadStatus()
                }}
              >
                Reset clock
              </Button>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="nb-label">Set needs</h3>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {NEED_FIELDS.map((field) => (
                <label key={field.key} className="space-y-1">
                  <span className="nb-label text-[9px]">{field.label}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={needs[field.key] ?? 0}
                    onChange={(event) =>
                      setNeeds((current) => ({ ...current, [field.key]: Number(event.target.value) }))
                    }
                    className="w-full rounded-[3px] border border-ash-600/60 bg-night-950/70 px-2 py-1.5 nb-numeric text-xs text-bone-200"
                  />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void devSetNeeds(needs)}
              >
                Apply needs
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  setNeeds({ hunger: 100, energy: 100, hygiene: 100, bond: 100, sanity: 100, fitness: 100 })
                }
              >
                Fill all
              </Button>
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="nb-label">Force mood</h3>
            <div className="flex flex-wrap gap-2">
              {MOOD_IDS.map((mood) => (
                <button
                  key={mood}
                  type="button"
                  disabled={busy}
                  onClick={() => void devSetMood(mood)}
                  className={cx(
                    'rounded-[3px] border border-ash-600/60 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.14em] text-ash-300 hover:bg-night-800 hover:text-bone-200',
                    creature?.visual.mood === mood && 'border-ember-600/70 text-ember-400',
                  )}
                >
                  {MOODS[mood].label}
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <h3 className="nb-label">State</h3>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void devSetSleep(!(creature?.sleeping ?? false))}
              >
                {creature?.sleeping ? 'Force wake' : 'Force sleep'}
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void devClearCooldowns()}>
                Clear cooldowns
              </Button>
            </div>
          </section>

          {creature ? (
            <section className="space-y-1 rounded-[3px] border border-ash-700/40 bg-night-950/60 p-3">
              <h3 className="nb-label">Readout</h3>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                <Readout label="Mood" value={creature.visual.mood} />
                <Readout label="State" value={creature.visual.state} />
                <Readout label="Activity" value={creature.visual.activity} />
                <Readout label="Sanity band" value={creature.visual.sanityBand} />
                <Readout label="Sleeping" value={String(creature.sleeping)} />
                <Readout label="Sleep hours" value={String(creature.sleepHours)} />
                <Readout label="In sleep window" value={String(creature.inSleepWindow)} />
                <Readout label="Server time" value={creature.serverTime.slice(11, 19)} />
              </dl>
              <p className="pt-1 nb-label text-[8px] text-ash-500">
                cooldowns: {Object.keys(creature.cooldowns).join(', ') || 'none active'}
              </p>
            </section>
          ) : null}
        </div>
      </Modal>
    </>
  )
}

function Readout({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-ash-400">{label}</dt>
      <dd className="nb-numeric text-bone-300">{value}</dd>
    </div>
  )
}
